"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useAuth } from "@/contexts/AuthContext";
import { useOrganisation } from "@/contexts/OrganisationContext";
import { NurseryItemDto } from "@/generated/model";
import {
  cacheSelectedNursery,
  getCachedSelectedNursery,
  useOfflineNurseryData,
} from "@/hooks/use-offline-nursery-data";
import { resolveSelectedNurseryId } from "@/lib/select-nursery";

export type NurseryRole =
  | "MANAGER"
  | "DEPUTY_MANAGER"
  | "LEADER"
  | "PRACTITIONER";

interface NurseryAccess {
  id: string;
  nurseryId: string;
  organisationId: string;
  role: string;
}

interface NurseryContextValue {
  // Selected nursery
  selectedNurseryId: string | null;
  selectedNursery: NurseryItemDto | null;
  setSelectedNursery: (nurseryId: string, orgId: string) => void;

  // Data for rendering the switcher
  nurseries: NurseryItemDto[];
  nurseriesByOrg: Record<string, NurseryItemDto[]>;

  // Nursery access data (moved from AuthContext)
  nurseryAccess: NurseryAccess[];
  nurseryRole: NurseryRole | null;

  // Role detection - Tier 3: Nursery Level (moved from AuthContext)
  isNurseryManager: boolean;
  isNurseryStaff: boolean;

  // Tier 3 nursery-level user (has direct nursery access, no org membership)
  isNurseryLevelUser: boolean;

  // Refresh nurseries list
  refreshNurseries: () => Promise<void>;

  // Loading states
  isLoading: boolean;
}

const NurseryContext = createContext<NurseryContextValue | null>(null);

export function NurseryProvider({ children }: { children: ReactNode }) {
  const {
    isPlatformAdmin,
    nurseryAccess: authNurseryAccess,
    isAuthenticated,
  } = useAuth();

  // Get organisation data from OrganisationContext
  const {
    setActiveOrg,
    memberships,
    isLoading: isOrgsLoading,
  } = useOrganisation();

  // Cast nurseryAccess from auth to our local type
  const nurseryAccess = authNurseryAccess as NurseryAccess[];

  // Compute nursery role detection (moved from AuthContext)
  const { nurseryRole, isNurseryManager, isNurseryStaff, defaultNurseryId } =
    useMemo(() => {
      if (nurseryAccess.length === 0) {
        return {
          nurseryRole: null,
          isNurseryManager: false,
          isNurseryStaff: false,
          defaultNurseryId: null,
        };
      }
      const access = nurseryAccess[0];
      const role = access?.role as NurseryRole | null;
      return {
        nurseryRole: role,
        isNurseryManager: role === "MANAGER",
        isNurseryStaff:
          role === "DEPUTY_MANAGER" ||
          role === "LEADER" ||
          role === "PRACTITIONER",
        defaultNurseryId: access?.nurseryId ?? null,
      };
    }, [nurseryAccess]);

  // Tier 3: Nursery-level user (has direct nursery access, no org membership)
  const isNurseryLevelUser =
    !isPlatformAdmin && memberships.length === 0 && nurseryAccess.length > 0;

  // Explicit selection made via the switcher this session
  const [explicitNurseryId, setExplicitNurseryId] = useState<string | null>(
    null,
  );

  // Last selection restored from the offline cache
  const [cachedNurseryId, setCachedNurseryId] = useState<string | null>(null);

  useEffect(() => {
    async function loadCachedSelection() {
      const cached = await getCachedSelectedNursery();
      if (cached?.nurseryId) {
        setCachedNurseryId(cached.nurseryId);
      }
    }
    loadCachedSelection();
  }, []);

  // Fetch ALL nurseries the user has access to (backend handles access control)
  const {
    nurseries,
    nurseriesByOrg,
    isLoading: isNurseriesLoading,
    refresh: refreshNurseries,
  } = useOfflineNurseryData(isAuthenticated);

  // Resolve the effective selection, validated against the loaded list so a
  // stale cached id can never leave the app without a nursery (NAU-335)
  const selectedNurseryId = useMemo(
    () =>
      resolveSelectedNurseryId({
        explicitId: explicitNurseryId,
        cachedId: cachedNurseryId,
        nurseryAccessDefaultId: defaultNurseryId,
        nurseries,
      }),
    [explicitNurseryId, cachedNurseryId, defaultNurseryId, nurseries],
  );

  // Find selected nursery object
  const selectedNursery = useMemo(() => {
    if (!selectedNurseryId) return null;
    return nurseries.find((n) => n.id === selectedNurseryId) || null;
  }, [selectedNurseryId, nurseries]);

  // Keep the active org and offline cache in sync with the resolved selection.
  // The isAuthenticated guard stops the effect re-seeding the offline cache
  // after logout has cleared it (signOut re-runs the effect while the nursery
  // list state is still populated).
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    if (!selectedNurseryId) {
      return;
    }
    const organisationId =
      nurseries.find((n) => n.id === selectedNurseryId)?.organisationId ??
      nurseryAccess.find((a) => a.nurseryId === selectedNurseryId)
        ?.organisationId ??
      null;
    if (!organisationId) {
      return;
    }
    setActiveOrg(organisationId);
    cacheSelectedNursery(selectedNurseryId, organisationId);
  }, [
    isAuthenticated,
    selectedNurseryId,
    nurseries,
    nurseryAccess,
    setActiveOrg,
  ]);

  // Handle nursery selection
  const setSelectedNursery = useCallback(
    (nurseryId: string, orgId: string) => {
      setExplicitNurseryId(nurseryId);
      // Update active org in OrganisationContext
      setActiveOrg(orgId);
    },
    [setActiveOrg],
  );

  const isLoading = isOrgsLoading || isNurseriesLoading;

  return (
    <NurseryContext.Provider
      value={{
        selectedNurseryId,
        selectedNursery,
        setSelectedNursery,
        refreshNurseries,
        nurseries,
        nurseriesByOrg,
        nurseryAccess,
        nurseryRole,
        isNurseryManager,
        isNurseryStaff,
        isNurseryLevelUser,
        isLoading,
      }}
    >
      {children}
    </NurseryContext.Provider>
  );
}

export function useNursery() {
  const context = useContext(NurseryContext);
  if (!context) {
    throw new Error("useNursery must be used within a NurseryProvider");
  }
  return context;
}
